/**
 * TIP Shell — bootstrap. The launch sequence (SCAFFOLD §2/§4):
 *   1. render the trusted-chrome shell
 *   2. load the origin map
 *   3. fetch the app's EffectiveGrant from the GATE (mount-time check) and enforce
 *      the COMPUTED tier — the app-declared tier is never trusted
 *   4. mint a per-launch nonce, build the SEALED iframe from SANDBOX_ORIGIN
 *   5. arm the Bridge (port handshake + second-load tripwire) and mount the frame
 *
 * Fail-closed everywhere: a Gate deny, a config error, or an exception mounts NO frame
 * and hands the frame NO identity.
 */
import { render } from 'preact';
import { App } from './ui.js';
import { loadConfig } from './config.js';
import { ServerClient } from './api.js';
import { createSealedFrame } from './seal.js';
import { Bridge, type BridgeHost } from './bridge.js';
import { NameOverlay } from './name-overlay.js';
import { mountFrame } from './dom.js';
import { actions, controller } from './store.js';
import { signInPage } from './ui.js';
import { onSessionEnded } from './api.js';

export async function mountShell(root: HTMLElement): Promise<void> {
  render(<App />, root);

  try {
    const cfg = await loadConfig();
    const api = new ServerClient(cfg.API_ORIGIN);

    const params = new URLSearchParams(location.search);
    const resourceId = /^\/r\/([0-9a-f]{8}-[0-9a-f-]{27})\/?$/i.exec(location.pathname)?.[1];
    // Only an opaque installation id survives SSO; no arbitrary redirect target or identity.
    if(resourceId) { try { sessionStorage.setItem('tip-resource-return',resourceId); } catch {} }

    const loginStatus = params.get('login'); // 'ok' | 'denied' | 'school_account' — set by the OIDC callback redirect
    // Why the sign-in screen is showing, when the Shell sent them there itself: they pressed
    // Sign out, or a call came back 401 mid-session. Read ONCE and stripped, so a refresh
    // does not say "you signed out" to someone who just arrived.
    const signin = params.get('signin'); // 'out' | 'expired' | null
    if (signin) {
      const u = new URL(location.href);
      u.searchParams.delete('signin');
      history.replaceState(null, '', u.toString());
    }
    // Any authenticated call answering 401 ⇒ the session is over. Go to the sign-in screen
    // and say so, rather than leaving a raw "HTTP 401" in the middle of a console.
    onSessionEnded(() => {if(resourceId){try{sessionStorage.setItem('tip-resource-return',resourceId);}catch{}}location.assign(signInPage('expired'));});

    // ── SSO session gate (fail-closed) ───────────────────────────────────────
    // The Shell mounts NOTHING without a resolved first-party session. No session ⇒
    // show the login gate and RETURN: no gate call, no seal, no frame, no identity.
    // A failed login round-trips back here as ?login=denied (no cookie) — surface it.
    // The probe retries a cold/booting server (see ServerClient.session). Say so out
    // loud while it waits: "waking the server" is the truth, and a silent 20-second
    // pause reads as a hang. A logged-out visitor never sees this — a 401 is definitive
    // and returns on the first attempt.
    const session = await api.session({
      onWaking: (n) => {
        actions.setStatus('waking the server — this happens after it has been idle…');
        actions.log('info', `/auth/session did not answer (attempt ${n}) — the instance is booting; retrying`);
      },
    });
    if (!session) {
      const loginUrl = `${cfg.API_ORIGIN}/auth/login`;
      if (signin === 'out') {
        actions.needLogin(loginUrl, 'You are signed out.', 'info');
      } else if (signin === 'expired') {
        actions.needLogin(loginUrl, 'Your session ended, so you were signed out. Sign in again to pick up where you left off.', 'info');
      } else if (loginStatus === 'school_account') {
        actions.needLogin(loginUrl, 'Sign in with your school Google account. Personal Google accounts can\'t be used here.');
        actions.log('warn', 'OIDC callback returned login=school_account — not a school-managed account (no hd); fail-closed');
      } else if (loginStatus === 'denied') {
        actions.needLogin(loginUrl, 'This account could not sign in. Use the school account registered with TIP, or ask your school’s TIP administrator for help.');
        actions.log('warn', 'OIDC callback returned login=denied — fail-closed (no session, no frame)');
      } else {
        actions.needLogin(loginUrl);
        actions.log('info', 'no SSO session — showing login gate (fail-closed: no gate call, no frame)');
      }
      return;
    }
    actions.log('info', `SSO session resolved — role=${session.role} (tenant-scoped; identity stays server-side)`);
    if(!resourceId) { try { const pending=sessionStorage.getItem('tip-resource-return');
      if(pending&&/^[0-9a-f]{8}-[0-9a-f-]{27}$/i.test(pending)){sessionStorage.removeItem('tip-resource-return');location.replace(`/r/${pending}`);return;}
    } catch {} } else { try { sessionStorage.removeItem('tip-resource-return'); } catch {} }
    if (session.role === 'teacher' || session.role === 'admin') {
      const {PilotFeedback} = await import('./pilot-feedback.js');
      const feedbackRoot = document.createElement('div');
      feedbackRoot.id = 'tip-pilot-feedback';
      document.body.append(feedbackRoot);
      render(<PilotFeedback api={api} session={session} />, feedbackRoot);
      // Native fullscreen hides siblings of the trusted preview wrapper. Move this
      // same root into it, preserving the note and the running activity's DOM.
      document.addEventListener('fullscreenchange', () => {
        const parent = document.fullscreenElement;
        (parent instanceof HTMLElement && parent.tagName !== 'IFRAME' ? parent : document.body).append(feedbackRoot);
        window.dispatchEvent(new Event('resize'));
      });
    }
    if(resourceId&&session.role!=='student'&&params.get('play')!=='1') {
      const {ResourceDashboard}=await import('./resources.js');render(<ResourceDashboard api={api} id={resourceId}/>,root);return;
    }
    actions.setRole(session.role); // presentation only (diagnostics visibility) — never authz

    // ── Student "Today" home (trusted chrome; NO sealed frame) ───────────────
    // A student with no explicit ?app= lands on their assignment list; picking
    // one re-enters this bootstrap with ?app=<slug> and mounts it sealed.
    if (session.role === 'student' && !params.get('app') && !resourceId) {
      const { StudentHome } = await import('./home.js');
      render(<StudentHome api={api} />, root);
      return;
    }

    // ── District console (admin seat; trusted chrome; NO sealed frame) ───────
    // Admins LAND on the District Console by default; ?view=teach drops them into
    // the Teacher Console, ?view=app into the sealed app view. Presentation-only
    // routing — every /admin endpoint re-checks admin server-side (not the authz
    // boundary). Non-admins never reach this branch.
    const view = params.get('view'); // 'app' | 'teach' | null
    if (session.role === 'admin' && view !== 'app' && view !== 'teach' && !resourceId) {
      const { DistrictConsole } = await import('./admin.js');
      const goto = (v: string) => {
        const u = new URL(location.href);
        u.searchParams.set('view', v);
        location.assign(u.toString());
      };
      render(
        <DistrictConsole
          api={api}
          session={session}
          onTeacherView={() => goto('teach')}
          onAppView={() => goto('app')}
        />,
        root,
      );
      return;
    }

    // ── Teacher console (trusted chrome; NO sealed frame) ────────────────────
    // Teachers/admins land on the console; ?view=app opens the student app view
    // (their writes are previews — is_preview — and never reach a gradebook).
    // Students NEVER see the console: the branch is role-gated here AND every
    // console endpoint re-checks teacher|admin server-side (this is UX routing,
    // not the authz boundary).
    if ((session.role === 'teacher' || session.role === 'admin') && view !== 'app' && !resourceId) {
      const { TeacherConsole } = await import('./console.js');
      render(
        <TeacherConsole
          api={api}
          session={session}
          onOpenApp={() => {
            const u = new URL(location.href);
            u.searchParams.set('view', 'app');
            location.assign(u.toString());
          }}
        />,
        root,
      );
      return;
    }

    let appId = params.get('app') ?? 'pythagorean-quest';
    const nav = params.get('nav'); // adversarial nav-vector selector (spike parity)
    const framePath = params.get('doc') ?? '/frame.html';
    actions.setApp(appId);

    // ── Mount-time effective-grant check (GATE) ──────────────────────────────
    // The Today home launches with ?assignment=<id>; the Gate verifies it
    // belongs to this app AND to a class the viewer is actually in.
    const assignmentId = params.get('assignment') ?? undefined;
    actions.setStatus(`fetching effective-grant for ${appId} …`);
    const mount = resourceId ? await api.mountResource(resourceId) : await api.gateMount(appId, assignmentId);
    if(resourceId&&mount.app_id){appId=mount.app_id;actions.setApp(mount.app_name??appId);}
    if (mount.deny || !mount.grant) {
      actions.deny(mount.reason ?? 'gate denied (fail-closed)');
      actions.log('warn', `GATE denied mount for ${appId}: ${mount.reason ?? '(no reason)'}`);
      return;
    }

    // The COMPUTED tier is authoritative — never an app-declared value.
    const tier = mount.grant.tier;
    // Name delivery into the frame is gated on the grant's computed exposed_fields (the
    // law), not tier alone — so a standard app that exposes no name gets a name-blind frame.
    const mayExposeFirstName = (mount.grant.exposed_fields ?? []).includes('first_name');
    const self = mount.self ?? { tag: 't_self' };
    const roster = mount.roster ?? [];
    actions.setTier(tier);
    actions.setSelfTag(self.tag); // the strip pins the viewer's own chip first
    actions.setInputForwarding(tier === 'standard');
    actions.setAppSeesFirstNames(tier === 'standard' && mayExposeFirstName); // same law as Bridge.showNames
    actions.setAppSeesFirstNames(tier === 'standard' && mayExposeFirstName); // same law as Bridge.showNames
    actions.log('info', `GATE grant: tier=${tier} hash=${mount.grant.effective_grant_hash.slice(0, 12)}… (computed, enforced)`);

    // ── Seal + Bridge ────────────────────────────────────────────────────────
    const nonce = crypto.randomUUID();
    const iframe = createSealedFrame({
      sandboxOrigin: cfg.SANDBOX_ORIGIN, framePath, tier, nonce,
      // A GENERATED app travels as its canonical uuid (the sandbox has no bundle for it
      // and fetches source by that id). A BUILT-IN app must keep its SLUG — the sandbox
      // resolves those through a hard allowlist. The server tells us which by returning
      // app_id + app_source_token only for generated apps.
      appId: mount.app_source_token && mount.app_id ? mount.app_id : appId, nav,
      cspReportToken: mount.csp_report_token ?? null,
      appSourceToken: mount.app_source_token ?? null,
    });

    // The name is painted OVER the app's own anchor (parent-DOM layer over the frame,
    // clamped to it — name-overlay.ts) AND kept as the chip in the strip above the frame.
    // The chip is kept on purpose: it sits outside the frame beside "Sealed — this app
    // can't see who you are", so it is the trust signal an app cannot imitate, and it is
    // the one accessible copy of the name (the overlay is aria-hidden so a screen reader
    // does not read the name twice). It is also the fallback for an app that never
    // reports its anchor's geometry.
    const overlay = new NameOverlay({ iframe, ariaHidden: true });
    const host: BridgeHost = {
      log: (k, m) => actions.log(k, m),
      paintSlot: (slotId, tag, name) => {
        actions.paintSlot(slotId, tag, name);
        // Only the viewer's OWN name is ever positioned in the app. The Bridge enforces
        // this (slot binding + generation); the host does not rely on it.
        if (tag === self.tag) overlay.setName(slotId, name);
      },
      placeSlot: (slotId, rect) => overlay.setRect(slotId, rect),
      paintLeaderboard: (slotId, rows) =>
        actions.paintLeaderboard(
          slotId,
          rows.map((r) => ({ tag: r.tag, name: r.given ?? null, score: r.score, maxScore: r.max_score })),
        ),
      setStatus: (msg) => actions.setStatus(msg),
      onBreach: (reason, killed) => actions.breach({ reason, killed }),
    };

    const bridge = new Bridge({
      iframe,
      sandboxOrigin: cfg.SANDBOX_ORIGIN,
      expectedNonce: nonce,
      tier,
      mayExposeFirstName,
      appId,
      self,
      roster,
      ...(mount.context ? { context: mount.context } : {}), // opaque activity scope → tip:init
      ...(mount.theme ? { theme: mount.theme } : {}), // the class's school look → tip:init
      api,
      host,
    });
    controller.onInput = (v: string) => bridge.forwardInput(v);

    bridge.start();
    mountFrame(iframe);
    // Attach trusted feedback only after a successful mount; it never enters Bridge options.
    if (session.role === 'student' && mount.library_feedback) render(<App api={api} feedback={mount.library_feedback} />, root);
    actions.setStatus(`sealed frame mounted (tier=${tier}, app=${appId}) — awaiting handshake`);
    // Redact the CSP report token from the visible log — the src carries ?rpt=, a
    // bearer credential; the Bridge Log must never display it.
    const srcForLog = (iframe.getAttribute('src') ?? '')
      .replace(/([?&]rpt=)[^&]*/, '$1[redacted]')
      .replace(/([?&]src=)[^&]*/, '$1[redacted]');
    actions.log('out', `iframe mounted — src=${srcForLog}`);
    actions.log('info', 'seal: sandbox="allow-scripts" referrerpolicy="no-referrer" allow="" (nothing else)');
  } catch (e) {
    // Name the failure the viewer actually hit. "ERROR at mount" for an unreachable
    // server told a teacher that TIP was broken when the truth was that the server had
    // not answered — two different problems with two different next steps.
    const msg = e instanceof Error ? e.message : String(e);
    const unreachable = /failed to fetch|networkerror|load failed|HTTP 50[234]/i.test(msg);
    actions.setStatus(
      unreachable
        ? 'Could not reach TIP after 30 seconds. Nothing was mounted — reload to try again.'
        : 'ERROR at mount — see console (fail-closed, no frame mounted)',
    );
    actions.log('warn', `mount failed: ${msg}`);
    console.error('[shell] mount failed', e);
  }
}
